import React from 'react'
import { Box, Button, Tooltip } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ApiPushCampaignReuse, ApiPushDestinations } from '../../apiService/Core/Apicall'
import AudienceBuilder from '../../Components/Push/AudienceBuilder'
import AudiencePreview from '../../Components/Push/AudiencePreview'
import ComposeForm, { ComposeErrors, ComposeValues, emptyCompose, validateCompose } from '../../Components/Push/ComposeForm'
import NotificationPreview from '../../Components/Push/NotificationPreview'
import PushDisabledNotice, { InlineAlert } from '../../Components/Push/PushDisabledNotice'
import PushPageHeader from '../../Components/Push/PushPageHeader'
import SendConfirmDialog from '../../Components/Push/SendConfirmDialog'
import TestSendDialog from '../../Components/Push/TestSendDialog'
import { headerBtnSx, outlineBtnSx, primaryBtnSx } from '../../Components/Push/pushStyles'
import { emptyAudience, getErrorMessage, isAudienceEmpty, isPushDisabled, toAudiencePayload } from '../../Components/Push/pushUtils'
import type { AudienceState, Campaign, PreviewResult, ReuseDraft } from '../../Components/Push/types'

const NewCampaign: React.FC = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const reuseId = searchParams.get('reuse') || ''
    const [reuseWarnings, setReuseWarnings] = React.useState<string[] | null>(null)
    const [reuseError, setReuseError] = React.useState<any>(null)
    const [audience, setAudience] = React.useState<AudienceState>(emptyAudience())
    const [values, setValues] = React.useState<ComposeValues>(emptyCompose())
    const [errors, setErrors] = React.useState<ComposeErrors>({})
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({})
    const [preview, setPreview] = React.useState<PreviewResult | null>(null)
    const [refreshKey, setRefreshKey] = React.useState(0)
    const [testOpen, setTestOpen] = React.useState(false)
    const [confirmOpen, setConfirmOpen] = React.useState(false)
    const [confirmSendable, setConfirmSendable] = React.useState(0)
    const [conflict, setConflict] = React.useState<string | null>(null)
    const [destinations, setDestinations] = React.useState<{ key: string; label: string }[]>([])
    const [destLoading, setDestLoading] = React.useState(true)
    const [loadError, setLoadError] = React.useState<any>(null)

    React.useEffect(() => {
        let cancelled = false
        ApiPushDestinations()
            .then((res) => { if (!cancelled) setDestinations(res?.data ?? []) })
            .catch((e) => { if (!cancelled) setLoadError(e) })
            .finally(() => { if (!cancelled) setDestLoading(false) })
        return () => { cancelled = true }
    }, [])

    // "Reuse & resend": /admin/notifications/new?reuse=<campaignId> pre-fills the form. It is still a normal send:
    // the audience is calculated again now and the admin must review and confirm it.
    React.useEffect(() => {
        if (!reuseId) return
        let cancelled = false
        ApiPushCampaignReuse(reuseId)
            .then((res) => {
                const d: ReuseDraft | undefined = res?.data
                if (cancelled || !d) return
                setValues({ title: d.title, body: d.body, destination: d.destination ?? '' })
                setAudience({
                    includeAll: d.audience.includeAll,
                    segments: d.audience.segments,
                    groupIds: d.audience.groupIds,
                    countries: d.audience.countries,
                    states: d.audience.states,
                    users: d.manualUsers,
                })
                setReuseWarnings(d.warnings ?? [])
            })
            .catch((e) => { if (!cancelled) setReuseError(e) })
        return () => { cancelled = true }
    }, [reuseId])

    const payload = React.useMemo(() => toAudiencePayload(audience), [audience])
    const empty = isAudienceEmpty(audience)
    const handleResult = React.useCallback((r: PreviewResult | null) => setPreview(r), [])

    const onValuesChange = (v: ComposeValues) => {
        setValues(v)
        setServerErrors({})
        if (Object.keys(errors).length) setErrors(validateCompose(v))
    }

    // validate the message first, then open the dialog
    const run = (action: 'test' | 'send') => {
        const errs = validateCompose(values)
        setErrors(errs)
        setServerErrors({})
        if (Object.keys(errs).length) return
        if (action === 'test') setTestOpen(true)
        else if (preview) {
            setConfirmSendable(preview.sendable)
            setConfirmOpen(true)
        }
    }

    const sendDisabledReason = empty
        ? 'Select an audience first'
        : !preview
            ? 'Waiting for audience preview'
            : preview.sendable <= 0
                ? 'No sendable users in this audience'
                : ''

    const trimmed = { title: values.title.trim(), body: values.body.trim(), destination: values.destination }

    return (
        <div className="common-layout flex-column">
            <PushPageHeader
                title="New campaign"
                actions={
                    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/notifications')} sx={headerBtnSx}>
                        Back
                    </Button>
                }
            />

            {isPushDisabled(loadError) ? (
                <PushDisabledNotice />
            ) : (
                <>
                    {loadError && <Box sx={{ mb: 2 }}><InlineAlert severity="error">{getErrorMessage(loadError)}</InlineAlert></Box>}
                    {reuseWarnings && (
                        <Box sx={{ mb: 2 }}>
                            <InlineAlert severity={reuseWarnings.length ? 'warning' : 'info'} onClose={() => setReuseWarnings(null)}>
                                Pre-filled from an earlier campaign. The audience is calculated again right now, so the counts can differ.
                                {reuseWarnings.map((w) => <div key={w}>{w}</div>)}
                            </InlineAlert>
                        </Box>
                    )}
                    {reuseError && (
                        <Box sx={{ mb: 2 }}>
                            <InlineAlert severity="error" onClose={() => setReuseError(null)}>
                                The earlier campaign could not be loaded: {getErrorMessage(reuseError)} You can still create a new one.
                            </InlineAlert>
                        </Box>
                    )}
                    {conflict && (
                        <Box sx={{ mb: 2 }}>
                            <InlineAlert severity="warning" onClose={() => setConflict(null)}>
                                {conflict} The audience preview has been refreshed; please review and confirm again.
                            </InlineAlert>
                        </Box>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3, alignItems: 'start' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                            <ComposeForm
                                values={values}
                                onChange={onValuesChange}
                                errors={errors}
                                serverErrors={serverErrors}
                                destinations={destinations}
                                destinationsLoading={destLoading}
                            />
                            <AudienceBuilder value={audience} onChange={setAudience} />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                            <AudiencePreview audience={payload} empty={empty} refreshKey={refreshKey} onResult={handleResult} />
                            <NotificationPreview title={values.title} body={values.body} />
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                <Button variant="contained" onClick={() => run('test')} sx={outlineBtnSx}>Send test</Button>
                                <Tooltip title={sendDisabledReason} disableHoverListener={!sendDisabledReason}>
                                    <span>
                                        <Button variant="contained" disabled={Boolean(sendDisabledReason)} onClick={() => run('send')} sx={primaryBtnSx}>
                                            Review &amp; send
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>

                    <TestSendDialog
                        open={testOpen}
                        onClose={() => setTestOpen(false)}
                        title={trimmed.title}
                        body={trimmed.body}
                        destination={trimmed.destination}
                    />
                    <SendConfirmDialog
                        open={confirmOpen}
                        onClose={() => setConfirmOpen(false)}
                        title={trimmed.title}
                        body={trimmed.body}
                        destination={trimmed.destination}
                        audience={payload}
                        sendable={confirmSendable}
                        onFieldErrors={(e) => { setServerErrors(e); setConfirmOpen(false) }}
                        onConflict={(m) => { setConflict(m); setRefreshKey((k) => k + 1) }}
                        onSent={(c: Campaign) => {
                            setConfirmOpen(false)
                            navigate(c?.id ? `/admin/notifications/${c.id}` : '/admin/notifications')
                        }}
                    />
                </>
            )}
        </div>
    )
}

export default NewCampaign
